import React, { ReactNode } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import Dashboard from "@/pages/Dashboard";
import UserManagement from "@/pages/UserManagement";
import LoginPage from "@/pages/LoginPage";
import "@/App.css";
import { ToastContainer } from "react-toastify";
import { getToken } from "@/utils/storage";
import GlobalLoadingOverlay from "@/components/GlobalLoadingOverlay";
import ModeManagement from "@/pages/modes/ModeManagement";
import NationManagement from "@/pages/nations/NationManagement";
import PlayerManagement from "@/pages/players/PlayerManagement";
import PlayerCareerPage from "@/pages/players/PlayerCareerPage";
import MatchManagement from "@/pages/matches/MatchManagement";
import LiveMatchScorer from "@/pages/live-scorer/LiveMatchScorer";
import PhaseManagement from "@/pages/phases/PhaseManagement";
import FantasyLeaderboard from "@/pages/FantasyLeaderboard/FantasyLeaderboard";
import LeagueManagement from "@/pages/leagues/LeagueManagement";
import TeamManagement from "@/pages/teams/TeamManagement";
import AdminTeamsPage from "@/pages/admin-teams/AdminTeamsPage";
import GameweekManagement from "./pages/gameweeks/GameweekManagement";
import TeamOfTheWeekPage from "./pages/gameweeks/TeamOfTheWeekPage";
import GameweekWinnersPage from "./pages/winners/GameweekWinnersPage";
import NotificationsPage from "./pages/notifications/NotificationsPage";
import AnnouncementsPage from "./pages/announcements/AnnouncementsPage";
import CareerPathPage from "./pages/quiz/CareerPathPage";
import McqPage from "./pages/quiz/McqPage";
import WhoAmIPage from "./pages/quiz/WhoAmIPage";
import QuizPlayersPage from "./pages/quiz/QuizPlayersPage";
import HigherOrLowerPage from "./pages/quiz/HigherOrLowerPage";
import MysteryClubPage from "./pages/quiz/MysteryClubPage";
import TrueFalsePage from "./pages/quiz/TrueFalsePage";
import TicTacToePage from "./pages/quiz/TicTacToePage";
import LastFanStandingPage from "./pages/lfs/LastFanStandingPage";
import DailyChallengePage from "./pages/daily-challenge/DailyChallengePage";
import ClubsPage from "./pages/clubs/ClubsPage";
import QuizTypesPage from "./pages/quiz/QuizTypesPage";
import StoriesPage from "./pages/stories/StoriesPage";
import ReportsPage from "./pages/quiz/ReportsPage";
import AbuseReportsPage from "./pages/reports/AbuseReportsPage";
import SocialCardsPage from "./pages/social-cards/SocialCardsPage";
import ApiFootballAudit from "./pages/tools/ApiFootballAudit";
import AnalyticsUsersPage from "./pages/analytics/AnalyticsUsersPage";
import AcademyContentPage from "./pages/academy/AcademyContentPage";
import AcademyStructurePage from "./pages/academy/AcademyStructurePage";

// 🔒 Types for route wrappers
interface RouteWrapperProps {
  children: ReactNode;
}

const ProtectedRoute: React.FC<RouteWrapperProps> = ({ children }) => {
  const isAuthenticated = getToken(); // Check if token exists to determine authentication status
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const PublicRoute: React.FC<RouteWrapperProps> = ({ children }) => {
  const isAuthenticated = getToken(); // Check if token exists to determine authentication status
  return isAuthenticated ? <Navigate to="/" replace /> : <>{children}</>;
};

const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/modes"
          element={
            <ProtectedRoute>
              <ModeManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/nations"
          element={
            <ProtectedRoute>
              <NationManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/players"
          element={
            <ProtectedRoute>
              <PlayerManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clubs"
          element={
            <ProtectedRoute>
              <ClubsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/players/:id/career"
          element={
            <ProtectedRoute>
              <PlayerCareerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gameweeks"
          element={
            <ProtectedRoute>
              <GameweekManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gameweeks/team-of-the-week"
          element={
            <ProtectedRoute>
              <TeamOfTheWeekPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/matches"
          element={
            <ProtectedRoute>
              <MatchManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/live-scorer"
          element={
            <ProtectedRoute>
              <LiveMatchScorer />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phases"
          element={
            <ProtectedRoute>
              <PhaseManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teams"
          element={
            <ProtectedRoute>
              <TeamManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-teams"
          element={
            <ProtectedRoute>
              <AdminTeamsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leagues"
          element={
            <ProtectedRoute>
              <LeagueManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leaderboard"
          element={
            <ProtectedRoute>
              <FantasyLeaderboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/winners"
          element={
            <ProtectedRoute>
              <GameweekWinnersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/announcements"
          element={
            <ProtectedRoute>
              <AnnouncementsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quiz/types"
          element={
            <ProtectedRoute>
              <QuizTypesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quiz/career-path"
          element={
            <ProtectedRoute>
              <CareerPathPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quiz/mcq"
          element={
            <ProtectedRoute>
              <McqPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quiz/club-league-history"
          element={
            <ProtectedRoute>
              <McqPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quiz/who-am-i"
          element={
            <ProtectedRoute>
              <WhoAmIPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quiz/players"
          element={
            <ProtectedRoute>
              <QuizPlayersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quiz/higher-or-lower"
          element={
            <ProtectedRoute>
              <HigherOrLowerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quiz/mystery-club"
          element={
            <ProtectedRoute>
              <MysteryClubPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quiz/true-or-false"
          element={
            <ProtectedRoute>
              <TrueFalsePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quiz/tictactoe"
          element={
            <ProtectedRoute>
              <TicTacToePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lfs"
          element={
            <ProtectedRoute>
              <LastFanStandingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/daily-challenge"
          element={
            <ProtectedRoute>
              <DailyChallengePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quiz/reports"
          element={
            <ProtectedRoute>
              <ReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/abuse"
          element={
            <ProtectedRoute>
              <AbuseReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stories"
          element={
            <ProtectedRoute>
              <StoriesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/social-cards"
          element={
            <ProtectedRoute>
              <SocialCardsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tools/api-football-audit"
          element={
            <ProtectedRoute>
              <ApiFootballAudit />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/users"
          element={
            <ProtectedRoute>
              <AnalyticsUsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/academy/structure"
          element={
            <ProtectedRoute>
              <AcademyStructurePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/academy/content"
          element={
            <ProtectedRoute>
              <AcademyContentPage />
            </ProtectedRoute>
          }
        />
        {/* 404 — anything unmatched falls back to dashboard if authed, login otherwise */}
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <Navigate to="/" replace />
            </ProtectedRoute>
          }
        />
      </Routes>
      <GlobalLoadingOverlay />
      <ToastContainer position="top-right" autoClose={3000} />
    </Router>
  );
};

export default App;
